<!--
  - Copyright (c) 2024. Haodong JU
  -->

<template>
  <div>
    <page-header>
      {{ $t('openTime') }}
      <template #subtitle>
        {{ $t('OpeningTimeReservationSub') }}
        <div
          v-if="!loading"
          :class="businessHourOffset === null ? 'orange--text text--darken-3' : ''"
          class="mt-1"
        >
          <template v-if="businessHourOffset !== null">
            {{ $t('openTimeActualTimeHint', { start: actualLabel('00:00') }) }}
          </template>
          <template v-else>
            {{ $t('openTimeBackendTimeHint') }}
          </template>
        </div>
      </template>
      <template #action>
        <primary-button
          :disabled="loadError || !editing"
          icon="mdi-content-save"
          text="save"
          @click="saveAll"
        />
      </template>
    </page-header>

    <v-alert
      v-if="loadError && !loading"
      class="mt-4"
      outlined
      type="error"
    >
      <div class="d-flex flex-wrap align-center">
        <div class="mr-4">
          {{ $t('openTimeLoadFailed') }}
        </div>
        <v-btn
          color="error"
          small
          text
          @click="reload"
        >
          {{ $t('openTimeRetry') }}
        </v-btn>
      </div>
    </v-alert>

    <div
      v-if="supportsEffectiveDate && !loadError"
      class="d-flex flex-wrap align-center mt-4"
      style="gap: 8px"
    >
      <v-chip
        v-for="s in sets"
        :key="s.key"
        :color="s.key === selectedKey ? 'primary' : 'grey lighten-3'"
        :text-color="s.key === selectedKey ? 'white' : 'black'"
        small
        @click="selectSet(s.key)"
      >
        <v-icon
          v-if="s.key !== DEFAULT_KEY"
          left
          small
        >
          mdi-calendar-range
        </v-icon>
        {{ setLabel(s) }}
        <template v-if="isExpired(s)">
          · {{ $t('openTimeExpired') }}
        </template>
      </v-chip>
      <v-btn
        color="primary"
        small
        text
        @click="addDatedSet"
      >
        <v-icon
          left
          small
        >
          mdi-plus
        </v-icon>
        {{ $t('openTimeAddDatedSet') }}
      </v-btn>
    </div>

    <div
      v-if="editing && !editing.isDefault"
      class="mt-4 pa-3 ot-dated-box"
    >
      <div class="text-body-2 mb-3">
        {{ $t('openTimeDatedSetHint') }}
      </div>
      <div
        class="d-flex flex-wrap align-start"
        style="gap: 0 12px"
      >
        <v-text-field
          v-model="editing.from"
          :hint="$t('openTimeDateHint')"
          :label="$t('openTimeEffectiveFrom')"
          class="ot-date-field"
          clearable
          dense
          outlined
          persistent-hint
          type="date"
        />
        <v-text-field
          v-model="editing.to"
          :hint="$t('openTimeDateHint')"
          :label="$t('openTimeEffectiveTo')"
          class="ot-date-field"
          clearable
          dense
          outlined
          persistent-hint
          type="date"
        />
        <v-spacer />
        <v-btn
          class="mt-1"
          color="red"
          small
          text
          @click="deleteDatedSet"
        >
          <v-icon
            left
            small
          >
            mdi-delete-outline
          </v-icon>
          {{ $t('openTimeDeleteSet') }}
        </v-btn>
      </div>
    </div>

    <div class="mt-4">
      <loading-provider :loading="loading">
        <template v-if="editing">
          <div
            v-for="w in editing.days"
            :key="w.dayOfWeek"
            class="pa-2 mb-2 ot-day"
          >
            <div class="ot-day-head">
              <div class="text-body-1 font-weight-bold ot-day-name">
                {{ w.name }}
              </div>
              <v-btn-toggle
                v-if="!editing.isDefault"
                v-model="w.mode"
                color="primary"
                dense
                mandatory
                @change="onModeChange(w)"
              >
                <v-btn
                  small
                  value="inherit"
                >
                  {{ $t('openTimeInherit') }}
                </v-btn>
                <v-btn
                  small
                  value="custom"
                >
                  {{ $t('openTimeCustom') }}
                </v-btn>
              </v-btn-toggle>
              <div
                v-if="w.mode === 'custom'"
                class="d-flex text-body-1 align-center"
              >
                <v-simple-checkbox
                  :value="w.openingTime.length===0"
                  @click="w.openingTime.length===0?addATime(w):w.openingTime=[]"
                />
                {{ $t('Close') }}
              </div>
            </div>
            <div
              v-if="w.mode === 'inherit'"
              class="text-body-2 grey--text text--darken-1 mt-2"
            >
              {{ $t('openTimeInheritSummary', { spans: defaultSummary(w.dayOfWeek) }) }}
            </div>
            <div
              v-else
              class="mt-2"
            >
              <div
                v-for="(o,i) in w.openingTime"
                :key="o.key"
                class="ot-span"
              >
                <business-time-select
                  v-model="o.from"
                  :label="$t('ReservationDayStart')"
                  :offset="businessHourOffset"
                  class="ot-span-field"
                  dense
                  hide-details
                />
                <business-time-select
                  v-model="o.to"
                  :label="$t('ReservationDayEnd')"
                  :offset="businessHourOffset"
                  class="ot-span-field"
                  dense
                  hide-details
                />
                <v-text-field
                  v-model="o.estimateDiningTimeModifier"
                  :label="$t('ReservationDayRuleEdit')"
                  class="ot-span-field"
                  dense
                  hide-details
                  outlined
                >
                  <template #append>
                    <div class="text-no-wrap text-body-1">
                      &times;15 min
                    </div>
                  </template>
                </v-text-field>
                <v-btn
                  v-if="i===0"
                  icon
                  @click="addATime(w)"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  icon
                  @click="removeTime(w,o.key)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </template>
      </loading-provider>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import WeekDays from '@/model/dataLayer/api/common/WeekDays'
import {
  deleteWeeklySettings,
  getAllWeeklySettings,
  saveWeeklySettings
} from '@/model/dataLayer/service/reservation/weeklySetting'
import PrimaryButton from '@/views/AadenPages/components/PrimaryButton.vue'
import BusinessTimeSelect from '@/views/AadenPages/settings/reserveSetting/openTime/BusinessTimeSelect.vue'
import {
  actualLabel,
  loadBusinessHourOffset,
  normalizeHHmm,
  spanMinutes
} from '@/views/AadenPages/settings/reserveSetting/businessTime'
import { sureToDelete } from '@/model/utils/utils'
import IKUtils from 'innerken-js-utils'
import dayjs from 'dayjs'

const DEFAULT_KEY = 'default'

let counter = 8478421

function setKey (from, to) {
  return (!from && !to) ? DEFAULT_KEY : `${from || ''}|${to || ''}`
}

function hasRange (row) {
  return !!(row.effectiveFrom || row.effectiveTo)
}

export default {
  name: 'ReservationOpenTime',
  components: {
    BusinessTimeSelect,
    PrimaryButton,
    LoadingProvider,
    PageHeader
  },
  data: function () {
    return {
      DEFAULT_KEY,
      loading: false,
      // 云端返回的全部营业时段（含带生效日期的）
      weeklySettingList: [],
      // 后端有 /all（cloud #437）才支持带生效日期的那几套
      supportsEffectiveDate: false,
      // 营业时段没读到（断网/5xx）：不显示表单、不让保存，免得把默认时段存回去覆盖真实数据
      loadError: false,
      businessHourOffset: null,
      selectedKey: DEFAULT_KEY,
      // 正在编辑的那一套：{ key, isDefault, isNew, from, to, days: [...] }
      editing: null,
      editingSnapshot: ''
    }
  },
  computed: {
    defaultRows () {
      return this.weeklySettingList.filter(it => !hasRange(it))
    },
    // 默认那套 + 每个生效区间一套；还没保存的新一套也列出来
    sets () {
      const map = new Map()
      map.set(DEFAULT_KEY, { key: DEFAULT_KEY, from: null, to: null })
      this.weeklySettingList.filter(hasRange).forEach(it => {
        const key = setKey(it.effectiveFrom, it.effectiveTo)
        if (!map.has(key)) map.set(key, { key, from: it.effectiveFrom ?? null, to: it.effectiveTo ?? null })
      })
      if (this.editing?.isNew) {
        map.set(this.editing.key, { key: this.editing.key, from: this.editing.from, to: this.editing.to, isNew: true })
      }
      const dated = [...map.values()].filter(s => s.key !== DEFAULT_KEY)
        .sort((a, b) => String(a.from || '').localeCompare(String(b.from || '')) || String(a.to || '').localeCompare(String(b.to || '')))
      return [map.get(DEFAULT_KEY), ...dated]
    },
    today () {
      return dayjs().format('YYYY-MM-DD')
    }
  },
  methods: {
    actualLabel (t) {
      return actualLabel(t, this.businessHourOffset)
    },
    setLabel (s) {
      if (s.key === DEFAULT_KEY) return this.$t('openTimeSetDefault')
      if (s.isNew && !s.from && !s.to) return this.$t('openTimeAddDatedSet')
      if (s.from && s.to) return this.$t('openTimeSetRange', { from: s.from, to: s.to })
      if (s.from) return this.$t('openTimeSetFrom', { from: s.from })
      return this.$t('openTimeSetTo', { to: s.to })
    },
    isExpired (s) {
      return !!s.to && s.to < this.today
    },
    defaultSummary (dayOfWeek) {
      const row = this.defaultRows.find(it => it.dayOfWeek === dayOfWeek)
      if (!row) return '-'
      const spans = row.openingTimeSpan ?? []
      if (spans.length === 0) return this.$t('Close')
      return spans.map(o => `${this.actualLabel(o.from)}–${this.actualLabel(o.to)}`).join(', ')
    },
    removeTime (w, key) {
      w.openingTime = w.openingTime.filter(it => it.key !== key)
    },
    addATime (w) {
      w.openingTime.push(this.creatDefaultOpeningTimeSpan())
    },
    creatDefaultOpeningTimeSpan () {
      return {
        key: counter++,
        estimateDiningTimeModifier: 0,
        from: '11:00',
        to: '23:00'
      }
    },
    // 云端返回的时段 → 页面编辑用（带 key；copy = true 时去掉 id，当成新的时段）
    toEditableSpans (spans, copy = false) {
      return (spans ?? []).map(o => {
        const span = { ...o, key: counter++ }
        if (copy) {
          delete span.id
          delete span.createTimestamp
        }
        return span
      })
    },
    onModeChange (w) {
      // 从「沿用默认」切到「单独设置」且还没有时段时，先把默认那套的这一天复制过来
      if (w.mode === 'custom' && w.openingTime.length === 0 && !w.rowId) {
        const row = this.defaultRows.find(it => it.dayOfWeek === w.dayOfWeek)
        w.openingTime = row ? this.toEditableSpans(row.openingTimeSpan, true) : [this.creatDefaultOpeningTimeSpan()]
      }
    },
    buildEditing (key) {
      const isDefault = key === DEFAULT_KEY
      const set = this.sets.find(s => s.key === key)
      const rows = this.weeklySettingList.filter(it => setKey(it.effectiveFrom, it.effectiveTo) === key)
      const days = WeekDays.getList().map(it => {
        const row = rows.find(w => w.dayOfWeek === it.id)
        if (isDefault) {
          // 默认那套跟原来一样：没设置过的周几先给一个 11:00–23:00
          return {
            dayOfWeek: it.id,
            name: it.name,
            rowId: row?.id ?? null,
            mode: 'custom',
            openingTime: row ? this.toEditableSpans(row.openingTimeSpan) : [this.creatDefaultOpeningTimeSpan()]
          }
        }
        return {
          dayOfWeek: it.id,
          name: it.name,
          rowId: row?.id ?? null,
          mode: row ? 'custom' : 'inherit',
          openingTime: row ? this.toEditableSpans(row.openingTimeSpan) : []
        }
      })
      return {
        key,
        isDefault,
        isNew: false,
        from: set?.from ?? null,
        to: set?.to ?? null,
        days
      }
    },
    snapshot () {
      if (!this.editing) return ''
      return JSON.stringify({
        from: this.editing.from,
        to: this.editing.to,
        days: this.editing.days.map(d => ({
          mode: d.mode,
          spans: d.openingTime.map(o => [o.from, o.to, String(o.estimateDiningTimeModifier)])
        }))
      })
    },
    isDirty () {
      return !!this.editing && (this.editing.isNew || this.snapshot() !== this.editingSnapshot)
    },
    async confirmDiscard () {
      if (!this.isDirty()) return true
      // 用项目里的确认框（门店 AdminV3 的 WebView 里 window.confirm 会直接返回 false）
      const res = await IKUtils.showConfirmAsyn('', this.$t('openTimeUnsaved'))
      return !!res?.isConfirmed
    },
    async selectSet (key) {
      if (key === this.selectedKey) return
      if (!(await this.confirmDiscard())) return
      this.selectedKey = key
      this.editing = this.buildEditing(key)
      this.editingSnapshot = this.snapshot()
    },
    async addDatedSet () {
      if (!(await this.confirmDiscard())) return
      const key = 'new-' + counter++
      this.selectedKey = key
      this.editing = {
        key,
        isDefault: false,
        isNew: true,
        from: null,
        to: null,
        days: WeekDays.getList().map(it => ({
          dayOfWeek: it.id,
          name: it.name,
          rowId: null,
          mode: 'inherit',
          openingTime: []
        }))
      }
      this.editingSnapshot = this.snapshot()
    },
    tip (message) {
      this.$store.commit('showTipsDialog', { tipsMessage: message })
    },
    validateEditing () {
      const e = this.editing
      if (!e.isDefault) {
        const from = e.from || null
        const to = e.to || null
        if (!from && !to) return this.$t('openTimeNeedDate')
        if (from && to && from > to) return this.$t('openTimeRangeReversed')
        const key = setKey(from, to)
        const originalKey = e.isNew ? null : e.key
        if (key !== originalKey && this.weeklySettingList.some(it => hasRange(it) && setKey(it.effectiveFrom, it.effectiveTo) === key)) {
          return this.$t('openTimeRangeDuplicate')
        }
        if (e.isNew && !e.days.some(d => d.mode === 'custom')) {
          return this.$t('openTimeNeedOneDay')
        }
      }
      for (const d of e.days) {
        if (d.mode !== 'custom') continue
        for (const o of d.openingTime) {
          if (spanMinutes(o.from, o.to) <= 0) {
            return this.$t('openTimeSpanInvalid', { day: d.name, start: this.actualLabel('00:00') })
          }
        }
      }
      return ''
    },
    toPayloadSpans (spans) {
      return spans.map(o => {
        const { key, ...rest } = o
        return {
          ...rest,
          from: normalizeHHmm(o.from) ?? o.from,
          to: normalizeHHmm(o.to) ?? o.to
        }
      })
    },
    async saveAll () {
      if (!this.editing || this.loadError) return
      const error = this.validateEditing()
      if (error) {
        this.tip(error)
        return
      }
      const e = this.editing
      const from = e.isDefault ? null : (e.from || null)
      const to = e.isDefault ? null : (e.to || null)
      // 逐天保存，某天失败就停下，并说清楚哪几天已经存了、哪几天没存
      const steps = e.days.filter(d => d.mode === 'custom' || d.rowId)
      const saved = []
      this.loading = true
      try {
        for (const [i, d] of steps.entries()) {
          let res
          if (d.mode === 'custom') {
            const item = {
              dayOfWeek: d.dayOfWeek,
              openingTimeSpan: this.toPayloadSpans(d.openingTime)
            }
            if (!e.isDefault) {
              // 带日期的那套：按 id 改这一行（也用来改生效区间），并带上区间
              if (d.rowId) item.id = d.rowId
              item.effectiveFrom = from
              item.effectiveTo = to
            }
            // 默认那套跟原来一样不带 id 和日期：云端按「周几 + 不带日期」覆盖，不碰带日期的那几套
            res = await saveWeeklySettings(item)
          } else {
            // 带日期的那套里改回「沿用默认」：删掉这一天的那一行
            res = await deleteWeeklySettings(d.rowId)
          }
          // hillo 出错时回 null；带 code 的看 code
          if (!res || (res.code !== undefined && res.code !== 200)) {
            const none = this.$t('openTimeNone')
            this.tip(this.$t('openTimePartialSave', {
              failed: d.name,
              message: res?.message || 'Error',
              saved: saved.map(x => x.name).join(', ') || none,
              unsaved: steps.slice(i).map(x => x.name).join(', ')
            }))
            return
          }
          if (d.mode !== 'custom') d.rowId = null
          saved.push(d)
        }
        this.selectedKey = setKey(from, to)
        await this.reload()
      } finally {
        this.loading = false
      }
    },
    async deleteDatedSet () {
      const e = this.editing
      if (!e || e.isDefault) return
      if (e.isNew) {
        this.selectedKey = DEFAULT_KEY
        this.editing = this.buildEditing(DEFAULT_KEY)
        this.editingSnapshot = this.snapshot()
        return
      }
      await sureToDelete(async () => {
        this.loading = true
        try {
          const rows = this.weeklySettingList.filter(it => setKey(it.effectiveFrom, it.effectiveTo) === e.key)
          for (const row of rows) {
            await deleteWeeklySettings(row.id)
          }
          this.selectedKey = DEFAULT_KEY
          await this.reload()
        } finally {
          this.loading = false
        }
      })
    },
    async reload () {
      this.loading = true
      try {
        const [settingResult, offsetResult] = await Promise.allSettled([
          getAllWeeklySettings(),
          loadBusinessHourOffset()
        ])
        this.businessHourOffset = offsetResult.status === 'fulfilled' ? offsetResult.value : null
        if (settingResult.status !== 'fulfilled') {
          this.loadError = true
          this.weeklySettingList = []
          this.supportsEffectiveDate = false
          this.editing = null
          this.editingSnapshot = ''
          return
        }
        this.loadError = false
        this.weeklySettingList = settingResult.value.list ?? []
        this.supportsEffectiveDate = settingResult.value.supportsEffectiveDate
        if (!this.sets.some(s => s.key === this.selectedKey)) {
          this.selectedKey = DEFAULT_KEY
        }
        this.editing = this.buildEditing(this.selectedKey)
        this.editingSnapshot = this.snapshot()
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.reload()
  }
}
</script>

<style scoped>
.ot-day {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.ot-day-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.ot-day-name {
  min-width: 90px;
}

.ot-span {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.ot-span-field {
  flex: 1 1 180px;
  max-width: 240px;
}

.ot-dated-box {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}

.ot-date-field {
  flex: 0 1 220px;
}

@media (max-width: 600px) {
  .ot-span-field {
    max-width: none;
    flex-basis: 40%;
  }
}
</style>
